import React from 'react';

import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import CloseIcon from '@material-ui/icons/Close';
import { Button, TextField, InputAdornment, DialogActions, createStyles, Theme, withStyles, IconButton, FormControl, InputLabel, Select, FormHelperText, MenuItem } from '@material-ui/core';

import style from './fwdAccounts.module.css';

import { transferTypeOptions } from '../../../utils/transferOptions';
import { formatCurrency } from '../../../utils/currencyFormatter';

import ForwarderAccountAndConfig from '../../../models/ForwarderAccountAndConfig';

const styles = (theme: Theme) =>

  createStyles({
    submitBtn: {
      margin: '10px 20px'
    },
    input: {
      padding: '11px 14px',
      color: 'var(--primaryblue)'
    },
    blue: {
      transform: 'translate(14px, 12px) scale(1)',
      color: 'var(--primaryblue)'
    },
    selectLabel: {
      transform: 'translate(14px, 29px) scale(1) ',
      color: 'var(--primaryblue)',
    },
    shrinkLabel: {
      transform: 'translate(14px, -11px) scale(0.75) !important',
    },
    select: {
      '& [class^="MuiSelect-select-"]': {
        padding: '11px 35px 11px 14px',
        color: 'var(--primaryblue)'
      }
    },
    accBal:{
      width: '25%',
      marginRight: ' 12px'
    },
    textField: {
      width: '25%',
      marginRight: ' 12px'
    },
    formControl: {
      width: '25%',
      marginRight: ' 12px'
    },
    textFieldNotes: {
      width: '30%'
    },
    '@media (max-width:991px)':{
      accBal:{
        width: 'calc(33.33% - 12px)',
        marginRight: ' 12px'
      },
      textField: {
        width: 'calc(33.33% - 0)',
      },
      formControl: {
        width: 'calc(33.33% - 12px)',
        marginRight: ' 12px'
      },
      textFieldNotes: {
        width: '100%',
        marginTop:'20px'
      },
    },
    '@media (max-width:768px)':{
      accBal:{
        width: '100%',
        marginRight: ' 0',
        marginBottom:' 20px',
      },
      textField: {
        width: '50%',
        marginRight:'0px'
      },
      formControl: {
        width: 'calc(50% - 12px)',
        marginRight: ' 12px',
      },
      textFieldNotes: {
        width: '100%',
        marginTop:'20px'
      },
    },
    '@media (max-width:480px)':{
      accBal:{
        width: '100%',
        marginRight: ' 0',
        marginBottom:'20px',
      },
      textField: {
        width: '100%',
        marginRight:'0px',
      },
      formControl: {
        width: '100%',
        marginRight: '0px',
        marginBottom:'20px',
      },
      textFieldNotes: {
        width: '100%',
        marginTop:'20px'
      },
    }
  });

interface FwdAccountBalanceDialogProps { 
  classes: any;
  forwarder: ForwarderAccountAndConfig;
  modalOpen: boolean;
  handleChange: (field: string, event: any) => void;
  handleCloseModal: () => void;
  handleSubmit: () => void;
  transferType: string;
  sum: string ;
  errors: {
    sum: string,
    notes: string
  };
  notes: string;
  infoMessage: string;
};

const fwdAccountBalanceDialog = (props: FwdAccountBalanceDialogProps) => {

  const { classes } = props;
  const transferOptions: Array<JSX.Element> = transferTypeOptions.map((item: {value: string, name: string}, index: number) => {
    return <MenuItem key={ index } value={ item.value }>{ item.name }</MenuItem>
  });

  return (
    <Dialog
      open={ props.modalOpen }
      onClose={ props.handleCloseModal }
      aria-labelledby="form-dialog-title"
      fullWidth
      maxWidth={ 'xl' }
    >
      <DialogTitle id="form-dialog-title">
        <div className={ style.dialogTitle }>
          <span>Account Balance</span>
          <IconButton aria-label="Close" onClick={ props.handleCloseModal} className={ classes.close }>
            <CloseIcon color='primary' fontSize='inherit'/>
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent className={ classes.dialogContent }>
        <div className={ style.fwdInfoWrapper }>
          <div>{ props.forwarder.name }</div>
          <div>{ props.forwarder.fwdInfo.address }</div>
          <div>E_mail: { props.forwarder.fwdInfo.email }</div>
          <div>Phone: { props.forwarder.fwdInfo.contacts ? props.forwarder.fwdInfo.contacts.join(' '): '' }</div>
        
        </div>
        <div className={ style.accountWrapper }>
          <div className={ classes.accBal } >
            <span className={ style.accountBalanceText }>Account Balance:</span>
            <span className={ style.accountBalanceSum }>{formatCurrency(props.forwarder.accountBalance, 2, '$')}</span> 
          </div>
          <FormControl className={ classes.formControl } variant="outlined">
            <InputLabel 
              htmlFor="transferType" 
              required
              className={ classes.selectLabel }
              classes={{
                shrink: classes.shrinkLabel
              }}
              shrink={ true }
            >
              Enter Transfer Type
            </InputLabel>
            <Select
              required
              onChange={ (event) => props.handleChange('transferType', event) }
              value={ props.transferType }
              className={ classes.select }
              inputProps={{ shrink: true }}  
            >
              { transferOptions }
            </Select>
          </FormControl>

          <TextField 
            required
            id="sum"
            label="Enter sum"
            value={props.sum}
            onChange={ () => props.handleChange('sum', event) }
            variant="outlined"
            InputProps={{
              classes: { input: classes.input },
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            InputLabelProps={{
              className: classes.blue
            }}
            className={ classes.textField }
            error={props.errors.sum !==''}
            helperText={props.errors.sum !=='' ? props.errors.sum  : ''}
          />

          <TextField
            required
            id="notes"
            label="Notes"
            value={ props.notes }
            onChange={ () => props.handleChange('notes', event) }
            variant="outlined"
            InputProps={{
              classes: { input: classes.input }
            }}
            InputLabelProps={{
              className: classes.blue
            }}
            className={ classes.textFieldNotes }
            error={ props.errors.notes !=='' }
            helperText={ props.errors.notes !=='' ? props.errors.notes : '' }
          />
        </div>
        
        <div className={ style.infoMessage }>
          { props.infoMessage && 
            <div>{ props.infoMessage }</div>
          }
        </div>
              
      </DialogContent>
      <DialogActions>
        <Button 
          className={ classes.submitBtn }
          variant="contained" 
          color="primary" 
          onClick={ props.handleSubmit }
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  )
};

export default withStyles(styles)(fwdAccountBalanceDialog);
